import posthog from 'posthog-js';
import { logger } from './logger';

// 通用埋点函数
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    logger.error('Failed to track analytics event:', error);
  }
};

// 页面埋点函数
export const trackPageView = (pageName: string, properties?: Record<string, any>) => {
  try {
    posthog.capture('$pageview', {
      page_name: pageName,
      ...properties
    });
  } catch (error) {
    logger.error('Failed to track page view:', error);
  }
};

// 首页埋点
export const trackEntryAgreementClicked = () => {
  trackEvent('entry_agreement_clicked', {
    selector_text: 'I AGREE, LET\'S BEGIN',
    page: '/'
  });
};

export const trackEntryLoginClicked = () => {
  trackEvent('entry_login_clicked', {
    selector_text: 'Login here',
    page: '/'
  });
};

// 问卷埋点
export const trackQuestionnaireStepComplete = (stepName: string, stepIndex: number) => {
  trackEvent('questionnaire_step_complete', {
    step_name: stepName,
    step_index: stepIndex,
    page: '/questionnaire'
  });
};

// 首页埋点
export const trackPhotoCheckClicked = () => {
  trackEvent('photo_check_clicked', {
    selector_text: 'How do I look?',
    page: '/home'
  });
};

export const trackPhotoUploadClicked = () => {
  trackEvent('photo_upload_clicked', {
    selector_text: 'Take or Upload Your Photo',
    page: '/home'
  });
};

export const trackPhotoCaptureClicked = () => {
  trackEvent('photo_capture_clicked', {
    selector_text: 'Capture',
    page: '/home'
  });
};

export const trackPhotoRetakeClicked = () => {
  trackEvent('photo_retake_clicked', {
    selector_text: 'Retake',
    page: '/home'
  });
};

export const trackPhotoAnalyzeClicked = () => {
  trackEvent('photo_analyze_clicked', {
    selector_text: 'Analyze',
    page: '/home'
  });
};

export const trackShareLinkClicked = () => {
  trackEvent('share_link_clicked', {
    selector_text: 'Share link',
    page: '/home'
  });
};

export const trackShareImagesClicked = () => {
  trackEvent('share_images_clicked', {
    selector_text: 'Share images',
    page: '/home'
  });
};

export const trackViewFullAnalysisClicked = () => {
  trackEvent('view_full_analysis_clicked', {
    selector_text: 'View my full style analysis',
    page: '/home'
  });
};

export const trackAiLabelClicked = (displayName: string) => {
  trackEvent('ai_label_clicked', {
    selector_text: `AI says I'm ${displayName}`,
    display_name: displayName,
    page: '/home'
  });
};

export const trackReportSectionClicked = (sectionName: string, dwellTime?: number) => {
  trackEvent('report_section_clicked_or_viewed', {
    section_name: sectionName,
    dwell_time: dwellTime,
    page: '/home'
  });
};

export const trackSaveToReportClicked = () => {
  trackEvent('save_to_report_clicked', {
    selector_text: 'Save to report',
    page: '/home'
  });
};

// My Stylist 页面埋点
export const trackSwitchGenderClicked = () => {
  trackEvent('switch_gender_clicked', {
    selector_text: 'Switch to Male version',
    page: '/my-stylist'
  });
};

export const trackCardSwiped = (swipeTime?: number) => {
  trackEvent('card_swiped', {
    swipe_time: swipeTime,
    page: '/my-stylist'
  });
};

export const trackStartPersonalizedClicked = () => {
  trackEvent('start_personalized_clicked', {
    selector_text: 'Start with more personalized details',
    page: '/my-stylist'
  });
};

export const trackShoppingLinkClicked = (productId?: string, section?: string) => {
  trackEvent('shopping_link_clicked', {
    product_id: productId,
    section: section,
    page: '/my-stylist'
  });
};

export const trackCustomizeClicked = () => {
  trackEvent('customize_clicked', {
    selector_text: 'Want to customize?',
    page: '/my-stylist'
  });
};

export const trackSeeAnotherBatchClicked = () => {
  trackEvent('see_another_batch_clicked', {
    selector_text: 'See another batch',
    page: '/my-stylist'
  });
};

export const trackDiscoverEntryClicked = (entryName: string) => {
  trackEvent('discover_entry_clicked', {
    entry_name: entryName,
    page: '/my-stylist'
  });
};

export const trackVibeLagDetected = (lagDuration: number) => {
  trackEvent('vibe_lag_detected', {
    lag_duration: lagDuration,
    page: '/my-stylist'
  });
};

// Chat 页面埋点
export const trackChatOptionClicked = (optionText: string, roundIndex: number) => {
  trackEvent('chat_option_clicked', {
    option_text: optionText,
    round_index: roundIndex,
    page: '/chat'
  });
};

export const trackQuickStartClicked = (chatRoundBeforeClick: number) => {
  trackEvent('quick_start_clicked', {
    chat_round_before_click: chatRoundBeforeClick,
    page: '/chat'
  });
};

export const trackChatShoppingLinkClicked = (productId?: string) => {
  trackEvent('shopping_link_clicked', {
    product_id: productId,
    page: '/chat'
  });
};

// Feedback 埋点
export const trackFeedbackEmojiClicked = (sentiment: 'positive' | 'negative', context: string, itemId?: string) => {
  trackEvent('feedback_emoji_clicked', {
    sentiment: sentiment,
    context: context,
    item_id: itemId,
    page: 'feedback'
  });
};

export const trackFeedbackTextWritten = (textLength: number, context: string, itemId?: string) => {
  trackEvent('feedback_text_written', {
    text_length: textLength,
    context: context,
    item_id: itemId,
    page: 'feedback'
  });
};

export const trackFeedbackSubmitted = (
  sentiment: 'positive' | 'negative', 
  hasComment: boolean, 
  commentLength?: number, 
  context?: string, 
  itemId?: string
) => {
  trackEvent('feedback_submitted', {
    sentiment: sentiment,
    has_comment: hasComment,
    comment_length: commentLength,
    context: context,
    item_id: itemId,
    page: 'feedback'
  });
};

// 性能监控埋点
export const trackPerformanceMetric = (metricName: string, value: number, extra?: Record<string, any>) => {
  trackEvent('performance_metric', {
    metric_name: metricName,
    value: value,
    ...extra
  });
};

// 错误埋点
export const trackError = (errorType: string, errorMessage: string, context?: string, extra?: Record<string, any>) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    context: context,
    ...extra
  });
};

// 表单输入埋点
export const trackFormInput = (field: string, valueLength: number, extra?: Record<string, any>) => {
  trackEvent('form_input', {
    field,
    value_length: valueLength,
    ...extra
  });
};

// 用户属性补充
export const identifyUser = (email: string, properties?: Record<string, any>) => {
  try {
    if (email) {
      // @ts-ignore
      window.posthog?.identify?.(email, properties);
    }
  } catch (error) {
    // ignore
  }
}; 

// 邀请码相关埋点
export const trackInviteCodeEntered = (inviteCode: string) => {
  trackEvent('invite_code_entered', {
    invite_code: inviteCode,
    page: '/signup'
  });
};

export const trackInviteCodeVerified = (inviteCode: string, isValid: boolean) => {
  trackEvent('invite_code_verified', {
    invite_code: inviteCode,
    is_valid: isValid,
    page: '/signup'
  });
};

export const trackInviteCodeUsed = (inviteCode: string, email: string) => {
  trackEvent('invite_code_used', {
    invite_code: inviteCode,
    user_email: email,
    page: '/signup'
  });
};

export const trackInviteCodeError = (inviteCode: string, errorType: string, errorMessage: string) => {
  trackEvent('invite_code_error', {
    invite_code: inviteCode,
    error_type: errorType,
    error_message: errorMessage,
    page: '/signup'
  });
};

export const trackInviteCodeAttemptLimit = (inviteCode: string, attemptCount: number) => {
  trackEvent('invite_code_attempt_limit_reached', {
    invite_code: inviteCode,
    attempt_count: attemptCount,
    page: '/signup'
  });
};

// 用户注册埋点（包含邀请码信息）
export const trackUserRegistration = (email: string, inviteCode: string, registrationMethod: 'email' | 'google') => {
  trackEvent('user_registration', {
    user_email: email,
    invite_code: inviteCode,
    registration_method: registrationMethod,
    page: '/signup'
  });
};

// 邀请码分组分析埋点
export const trackInviteCodeGroupAnalysis = (inviteCode: string, groupType: string) => {
  trackEvent('invite_code_group_analysis', {
    invite_code: inviteCode,
    group_type: groupType, // 例如: 'TRUSTCORE', 'STYLEKEY', 'GENZALPHA' 等
    page: '/signup'
  });
}; 